import React, { useState, useRef, useEffect } from "react";
import {
  Mic,
  Send,
  X,
  Maximize2,
  Minimize2,
  Copy,
  RotateCcw,
  ArrowLeft,
  ArrowRight,
  Check,
  ExternalLink,
  TrendingUp,
  TrendingDown,
  ChevronDown,
  ChevronRight,
} from "lucide-react";
import { apiCall } from "@/api/apiCall";
import { useNavigate } from "react-router-dom";
import Chatbot from "@/assets/img/Chatbot.png";
import { useChatbot } from "@/context/ChatbotContext";

const nowTime = () =>
  new Date().toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });

const GENERIC_ERROR = "Something went wrong. Please try again.";

const getErrorText = (response, fallback = GENERIC_ERROR) => {
  const msg = response?.message;
  if (!msg) return fallback;
  if (/invalid cube/i.test(msg)) {
    return "This dataset isn't available. Please select a valid one.";
  }
  return msg;
};

const toAmbiguousList = (filters) =>
  (filters || []).map((f) => ({
    cube: f.name,
    label: f.name,
    code: f.code,
  }));

const extractCubes = (response) => {
  if (Array.isArray(response?.cubes_new) && response.cubes_new.length > 0) {
    return response.cubes_new;
  }
  if (Array.isArray(response?.cubes) && response.cubes.length > 0) {
    return response.cubes.map((c) =>
      typeof c === "string" ? { cube: c, label: c } : c
    );
  }
  return [];
};

/* -------------------------------------------------------------------------- */
/*                          FORMATTED DATA RENDERERS                          */
/* -------------------------------------------------------------------------- */

const SUPPORTED_TYPES = new Set([
  // "SIMPLE_INFO",
  "DIMENSION_COMPARISON",
  "RANKING",
  "ANALYSIS",
  "COMPARISON",
  "TREND",
]);

// Validates that formatted_data is structured in a way we can render.
// If false, caller falls back to showing the summary text.
const hasRenderableFormat = (fd) => {
  if (!fd || typeof fd !== "object") return false;
  if (!SUPPORTED_TYPES.has(fd.type)) return false;
  if (!fd.data || typeof fd.data !== "object") return false;
  return true;
};

const formatExact = (raw) => {
  const num = Number(raw);
  if (!Number.isFinite(num)) return String(raw);
  const truncated = Math.floor(num * 100) / 100;
  return truncated.toLocaleString("en-US", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
};

/* Shared header: title only */
const CardHeader = ({ title, isMax }) => (
  <p
    className={`font-semibold text-foreground leading-snug break-words ${
      isMax ? "text-base" : "text-sm"
    }`}
  >
    {title}
  </p>
);

/* ------------------------------ SIMPLE_INFO ------------------------------ */
const SimpleInfoCard = ({ d, isMax }) => (
  <div className={isMax ? "space-y-4" : "space-y-3"}>
    <CardHeader type="SIMPLE_INFO" title={d.title} isMax={isMax} />

    <div
      className={`rounded-xl bg-primary/5 border border-primary/15 ${
        isMax
          ? "px-6 py-5 flex flex-wrap items-end justify-between gap-3"
          : "px-4 py-3"
      }`}
    >
      <p
        className={`font-semibold tracking-tight leading-tight break-words ${
          isMax ? "text-5xl" : "text-1xl"
        }`}
      >
        {d.value}
      </p>
      {d.raw != null && (
        <p
          className={`text-muted-foreground truncate ${
            isMax ? "text-sm" : "text-[11px] mt-1.5"
          }`}
        >
          Exact value: {formatExact(d.raw)}
        </p>
      )}
    </div>
  </div>
);

/* -------------------------- DIMENSION_COMPARISON (TABLE) ------------------------- */
const DimensionComparisonCard = ({ d, isMax }) => {
  const rows = d.rows || [];

  // Saare rows se unique column keys nikalo (order preserve karke)
  const columns = Array.from(
    new Set(rows.flatMap((row) => (row.values || []).map((v) => v.key)))
  );

  const getValue = (row, col) =>
    row.values?.find((v) => v.key === col)?.value ?? "—";

  return (
    <div className={isMax ? "space-y-4" : "space-y-3"}>
      <CardHeader type="DIMENSION_COMPARISON" title={d.title} isMax={isMax} />

{rows.length > 0 && (
      <div className="overflow-x-auto rounded-xl border border-border/60 cb-scroll">
        <table
          className={`w-full border-collapse ${isMax ? "text-sm" : "text-xs"}`}
        >
          <thead>
            <tr className="bg-muted/60 border-b border-border/60">
              <th className="text-left font-semibold text-foreground px-4 py-3 whitespace-nowrap bg-muted">
                Name
              </th>
              {columns.map((col) => (
                <th
                  key={col}
                  className="text-right font-semibold text-foreground px-4 py-3 whitespace-nowrap bg-muted"
                >
                  {col}
                </th>
              ))}
            </tr>
          </thead>

          <tbody>
            {rows.map((row, i) => (
              <tr
                key={i}
                className="border-b border-border/40 last:border-b-0 hover:bg-muted/30 transition-colors"
              >
                <td className="text-left font-medium text-foreground px-4 py-3 whitespace-nowrap">
                  {row.label}
                </td>
                {columns.map((col) => (
                  <td
                    key={col}
                    className="text-right text-foreground tabular-nums px-4 py-3 whitespace-nowrap"
                  >
                    {getValue(row, col)}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
)}

      {d.highlights && (
        <div className={`grid gap-2 ${isMax ? "grid-cols-2" : "grid-cols-1"}`}>
          {d.highlights.highest && (
            <div
              className={`flex items-center gap-2 rounded-lg bg-emerald-500/10 px-3 py-2 ${
                isMax ? "text-sm" : "text-xs"
              }`}
            >
              <TrendingUp size={14} className="text-emerald-600 flex-shrink-0" />
              <span className="text-muted-foreground">Highest</span>
              <span className="ml-auto font-medium text-foreground text-right">
                {d.highlights.highest.label} ({d.highlights.highest.value})
              </span>
            </div>
          )}
          {d.highlights.lowest && (
            <div
              className={`flex items-center gap-2 rounded-lg bg-red-500/10 px-3 py-2 ${
                isMax ? "text-sm" : "text-xs"
              }`}
            >
              <TrendingDown size={14} className="text-red-500 flex-shrink-0" />
              <span className="text-muted-foreground">Lowest</span>
              <span className="ml-auto font-medium text-foreground text-right">
                {d.highlights.lowest.label} ({d.highlights.lowest.value})
              </span>
            </div>
          )}
        </div>
      )}

      {d.truncated?.message && (
        <div className="mt-3 p-3 bg-blue-500/10 border border-blue-200 dark:border-blue-900 rounded-lg">
          <p className="text-xs text-muted-foreground italic">
            {d.truncated.message}
          </p>
        </div>
      )}
    </div>
  );
};
/* ----------------------- COLLAPSIBLE HEADER (shared) ----------------------- */
const CollapsibleHeader = ({ open, onToggle, children, className = "" }) => (
  <button
    type="button"
    onClick={onToggle}
    className={`w-full flex items-center gap-2 text-left ${className}`}
  >
    {open ? (
      <ChevronDown size={16} className="flex-shrink-0 text-muted-foreground" />
    ) : (
      <ChevronRight size={16} className="flex-shrink-0 text-muted-foreground" />
    )}
    {children}
  </button>
);

/* ----------------------------- STREET SECTION ----------------------------- */
const StreetSection = ({ street, isMax }) => {
  const [open, setOpen] = useState(true);
  const items = street.items || [];
  const count = items.length;
  const countText = `${count} ${count === 1 ? "device" : "devices"}`;

  return (
    <div className="space-y-1.5">
      {street.label ? (
        <button
  type="button"
  onClick={() => setOpen((o) => !o)}
  className="w-full flex items-start gap-2 rounded-lg border border-border/60 bg-background px-3 py-2 text-left hover:bg-muted/40 transition-colors"
>
  <span className="mt-0.5 flex-shrink-0">
    {open ? (
      <ChevronDown size={14} className="text-muted-foreground" />
    ) : (
      <ChevronRight size={14} className="text-muted-foreground" />
    )}
  </span>
  <span
    className={`flex-1 min-w-0 font-medium text-foreground break-words leading-snug ${
      isMax ? "text-sm" : "text-xs"
    }`}
  >
    {street.label}
  </span>
  <span className="mt-0.5 text-[11px] text-muted-foreground whitespace-nowrap flex-shrink-0">
    {countText}
  </span>
</button>
      ) : null}

      {(open || !street.label) && (
        <ol
          className={`flex flex-col gap-1.5 ${
            street.label ? "pl-3 ml-2 border-l-2 border-border/60" : ""
          }`}
        >
          {items.map((item) => (
            <RankItem
              key={`${item.rank}-${item.code}`}
              item={item}
              isMax={isMax}
            />
          ))}
        </ol>
      )}
    </div>
  );
};

/* ------------------------------ GROUP SECTION ------------------------------ */
const GroupSection = ({ group, isMax }) => {
  const [open, setOpen] = useState(true);
  const streets =
    Array.isArray(group.streets) && group.streets.length > 0
      ? group.streets
      : [{ label: null, items: group.items || [] }];

  return (
    <div className="rounded-xl border border-border/60 bg-background/60 overflow-hidden">
      <div
        className={`flex items-center justify-between gap-3 bg-muted/60 ${
          open ? "border-b border-border/60" : ""
        } ${isMax ? "px-4 py-3" : "px-3 py-2"}`}
      >
        <CollapsibleHeader
          open={open}
          onToggle={() => setOpen((o) => !o)}
          className="min-w-0"
        >
          <span
            className={`font-semibold text-foreground truncate ${
              isMax ? "text-sm" : "text-xs"
            }`}
          >
            {group.label}
          </span>
        </CollapsibleHeader>

        {group.subtotal?.value != null && (
          <p
            className={`font-semibold text-foreground tabular-nums flex-shrink-0 ${
              isMax ? "text-sm" : "text-xs"
            }`}
          >
            {group.subtotal.value}
          </p>
        )}
      </div>

      {open && (
        <div className="p-2 space-y-3">
          {streets.map((street, sIdx) => (
            <StreetSection
              key={`${street.label}-${sIdx}`}
              street={street}
              isMax={isMax}
            />
          ))}
        </div>
      )}
    </div>
  );
};

/* ------------------------------- RANKING ITEM ------------------------------- */
const RankItem = ({ item, isMax }) => (
  <li
    className={`flex items-center gap-3 rounded-xl border bg-muted/50 border-border/60 ${
      isMax ? "px-4 py-2.5" : "px-3 py-2"
    }`}
  >
    <span
      className={`flex items-center justify-center rounded-full font-semibold flex-shrink-0 bg-background text-muted-foreground border border-border ${
        isMax ? "w-7 h-7 text-xs" : "w-6 h-6 text-[11px]"
      }`}
    >
      {item.rank}
    </span>
    <div className="flex-1 min-w-0">
      <p
        className={`font-medium text-foreground truncate ${
          isMax ? "text-sm" : "text-xs"
        }`}
      >
        {item.label}
      </p>
      {item.code && (
        <p className="text-[11px] text-muted-foreground truncate">
          {item.code}
        </p>
      )}
    </div>
    <p
      className={`font-semibold text-foreground tabular-nums flex-shrink-0 ${
        isMax ? "text-sm" : "text-xs"
      }`}
    >
      {item.value}
    </p>
  </li>
);

/* -------------------------------- RANKING -------------------------------- */
const RankingCard = ({ d, isMax }) => {
  const items = d.items || [];
  const twoCols = isMax && items.length > 5;

  const groups = (Array.isArray(d.groups) ? d.groups : []).filter(
    (g) =>
      g &&
      ((Array.isArray(g.streets) && g.streets.length > 0) ||
        (Array.isArray(g.items) && g.items.length > 0))
  );
  const hasGroups = groups.length > 0;

  return (
    <div className={isMax ? "space-y-4" : "space-y-3"}>
      <CardHeader type="RANKING" title={d.title} isMax={isMax} />

      {hasGroups ? (
        <div className="space-y-4">
          {groups.map((group, gIdx) => (
            <GroupSection
              key={`${group.label}-${gIdx}`}
              group={group}
              isMax={isMax}
            />
          ))}
        </div>
      ) : (
        <ol
          className={`gap-1.5 ${
            twoCols ? "grid grid-cols-1 lg:grid-cols-2" : "flex flex-col"
          }`}
        >
          {items.map((item) => (
            <RankItem key={item.rank} item={item} isMax={isMax} />
          ))}
        </ol>
      )}
    </div>
  );
};

/* ------------------------------- ANALYSIS -------------------------------- */
const AnalysisCard = ({ d, isMax }) => {
  const isUp = d.variance?.direction === "up";
  const pct = d.variance?.percentage;

  // Sirf driver_groups use hoga; jis group me drivers nahi hain wo skip
  const groups = (Array.isArray(d.driver_groups) ? d.driver_groups : []).filter(
    (g) => Array.isArray(g.drivers) && g.drivers.length > 0
  );
  const hasGroups = groups.length > 0;

  const summary = (
    <div
      className={`rounded-xl border ${isMax ? "p-5" : "p-3.5"} ${
        isUp
          ? "bg-emerald-500/10 border-emerald-600/25"
          : "bg-red-500/10 border-red-500/25"
      }`}
    >
      <p
        className={`flex items-center gap-2 font-semibold ${
          isMax ? "text-xl" : "text-base"
        } ${
          isUp
            ? "text-emerald-700 dark:text-emerald-400"
            : "text-red-600 dark:text-red-400"
        }`}
      >
        {isUp ? (
          <TrendingUp size={isMax ? 20 : 16} />
        ) : (
          <TrendingDown size={isMax ? 20 : 16} />
        )}
        <span className="tabular-nums">
          {d.variance?.absolute?.value}
          {pct != null && ` (${Number(pct).toFixed(2)}%)`}
        </span>
      </p>

      <div className="grid grid-cols-2 gap-2 mt-3">
        {[d.periods?.previous, d.periods?.current].map((p, i) => (
          <div key={i} className="rounded-lg bg-background/70 px-3 py-2">
            <p className="text-[11px] text-muted-foreground">{p?.label}</p>
            <p
              className={`font-semibold text-foreground tabular-nums ${
                isMax ? "text-base" : "text-sm"
              }`}
            >
              {p?.value}
            </p>
          </div>
        ))}
      </div>
    </div>
  );

  const groupsView = hasGroups && (
    <div className="space-y-4">
      {groups.map((group, gIdx) => (
        <div key={group.role || gIdx} className="space-y-2">
          {/* Title ab API (driver_groups[].title) se aata hai */}
          {group.title && (
            <p
              className={`font-medium text-muted-foreground ${
                isMax ? "text-sm" : "text-xs"
              }`}
            >
              {group.title}
            </p>
          )}

          {group.drivers.map((dr, i) => {
            const drUp = dr.direction === "up";
            return (
              <div
                key={`${dr.name}-${i}`}
                className="rounded-xl bg-muted/50 border border-border/60 px-3 py-2.5"
              >
                <p
                  className={`font-medium text-foreground mb-1 ${
                    isMax ? "text-sm" : "text-xs"
                  }`}
                >
                  {dr.name}
                </p>
                <div className="flex justify-between items-center gap-3 text-xs">
                  <span className="text-muted-foreground tabular-nums">
                    {dr.change?.value}{" "}
                    {dr.change_pct != null &&
                      `(${Number(dr.change_pct).toFixed(2)}%)`}
                  </span>
                  <span
                    className={`inline-flex items-center gap-1 font-medium ${
                      drUp
                        ? "text-emerald-600 dark:text-emerald-400"
                        : "text-red-500 dark:text-red-400"
                    }`}
                  >
                    {drUp ? (
                      <TrendingUp size={12} />
                    ) : (
                      <TrendingDown size={12} />
                    )}
                    {drUp ? "Positive" : "Negative"}
                  </span>
                </div>
              </div>
            );
          })}
        </div>
      ))}
    </div>
  );

  return (
    <div className={isMax ? "space-y-4" : "space-y-3"}>
      <CardHeader type="ANALYSIS" title={d.title} isMax={isMax} />
      {isMax && hasGroups ? (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 items-start">
          {summary}
          {groupsView}
        </div>
      ) : (
        <>
          {summary}
          {groupsView}
        </>
      )}
      
      {/* Notice — only when present */}
      {d.notice && (
        <div className="mt-3 p-3 bg-blue-500/10 border border-blue-200 dark:border-blue-900 rounded-lg">
          <p className="text-xs text-muted-foreground italic">{d.notice}</p>
        </div>
      )}
    </div>
  );
};

/* ------------------------------ COMPARISON (TABLE FORMAT - FINAL) ------------------------------- */
const ComparisonCard = ({ d, isMax }) => {
  // Detect format: simple metrics or grouped metrics
  const hasGroups = Array.isArray(d.groups) && d.groups.length > 0;
  const metrics = d.metrics || [];

  // ============================================================================
  // FORMAT 1: SIMPLE METRICS TABLE
  // ============================================================================
  if (!hasGroups && metrics.length > 0) {
    return (
      <div className={isMax ? "space-y-4" : "space-y-3"}>
        <CardHeader type="COMPARISON" title={d.title} isMax={isMax} />

        {/* Scrollable table container - allows horizontal scroll for year columns */}
        <div className="overflow-x-auto rounded-xl border border-border/60 cb-scroll">
          <table
            className={`w-full border-collapse ${isMax ? "text-sm" : "text-xs"}`}
          >
            {/* Table Header */}
            <thead>
              <tr className="bg-muted/60 border-b border-border/60">
                <th className="text-left font-semibold text-foreground px-4 py-3 whitespace-nowrap">
                  Metric
                </th>
                {/* Year Columns - SCROLLABLE */}
                <th className="text-right font-semibold text-foreground px-4 py-3 whitespace-nowrap">
                  {d.periods?.previous}
                </th>
                <th className="text-right font-semibold text-foreground px-4 py-3 whitespace-nowrap">
                  {d.periods?.current}
                </th>
                <th className="text-right font-semibold text-foreground px-4 py-3 whitespace-nowrap">
                  Change
                </th>
                <th className="text-center font-semibold text-foreground px-4 py-3 whitespace-nowrap">
                  %
                </th>
              </tr>
            </thead>

            {/* Table Body */}
            <tbody>
              {metrics.map((m, idx) => {
                const isUp = m.variance?.direction === "up";
                return (
                  <tr
                    key={idx}
                    className="border-b border-border/40 last:border-b-0 hover:bg-muted/30 transition-colors"
                  >
                    <td className="text-left text-foreground tabular-nums px-4 py-3 whitespace-nowrap">
                      {m.name}
                    </td>

                    {/* Previous Value - Gray/Normal */}
                    <td className="text-right text-foreground tabular-nums px-4 py-3 whitespace-nowrap">
                      {m.previous?.value ?? "—"}
                    </td>

                    {/* Current Value - Same Color as Previous (NOT BLUE) */}
                    <td className="text-right text-foreground font-medium tabular-nums px-4 py-3 whitespace-nowrap">
                      {m.current?.value ?? "—"}
                    </td>

                    {/* Absolute Change */}
                    <td
                      className={`text-right font-medium tabular-nums px-4 py-3 whitespace-nowrap ${
                        isUp
                          ? "text-emerald-600 dark:text-emerald-400"
                          : "text-red-600 dark:text-red-400"
                      }`}
                    >
                      {isUp ? "+" : ""}
                      {m.variance?.absolute?.value}
                    </td>

                    {/* Percentage */}
                    <td
                      className={`text-center font-medium tabular-nums px-4 py-3 whitespace-nowrap ${
                        isUp
                          ? "text-emerald-600 dark:text-emerald-400"
                          : "text-red-600 dark:text-red-400"
                      }`}
                    >
                      {isUp ? "▲" : "▼"}{" "}
                      {m.variance?.percentage != null &&
                        `${Math.abs(Number(m.variance.percentage)).toFixed(2)}%`}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>
    );
  }

  // ============================================================================
  // FORMAT 2: GROUPED METRICS (Multiple Tables)
  // ============================================================================
  if (hasGroups) {
    return (
      <div className={isMax ? "space-y-4" : "space-y-3"}>
        <CardHeader type="COMPARISON" title={d.title} isMax={isMax} />

        {/* Render a table for each group */}
        <div className={isMax ? "space-y-4" : "space-y-3"}>
          {d.groups.map((group, gIdx) => (
            <div key={gIdx} className="space-y-2">
              {/* Group Header */}
              <div className="flex items-baseline gap-2 px-1">
                <h4
                  className={`font-semibold text-foreground ${
                    isMax ? "text-base" : "text-sm"
                  }`}
                >
                  {group.label}
                </h4>
              </div>

              {/* Group Table */}
              <div className="overflow-x-auto rounded-xl border border-border/60 cb-scroll">
                <table
                  className={`w-full border-collapse ${
                    isMax ? "text-sm" : "text-xs"
                  }`}
                >
                  <thead>
                    <tr className="bg-muted/60 border-b border-border/60">
                      <th className="text-left font-semibold text-foreground px-4 py-2.5 whitespace-nowrap">
                        Metric
                      </th>
                      {/* Year Columns - SCROLLABLE */}
                      <th className="text-right font-semibold text-foreground px-4 py-2.5 whitespace-nowrap">
                        {d.periods?.previous}
                      </th>
                      <th className="text-right font-semibold text-foreground px-4 py-2.5 whitespace-nowrap">
                        {d.periods?.current}
                      </th>
                      <th className="text-right font-semibold text-foreground px-4 py-2.5 whitespace-nowrap">
                        Change
                      </th>
                      <th className="text-center font-semibold text-foreground px-4 py-2.5 whitespace-nowrap">
                        %
                      </th>
                    </tr>
                  </thead>

                  <tbody>
                    {(group.metrics || []).map((m, mIdx) => {
                      const isUp = m.variance?.direction === "up";
                      return (
                        <tr
                          key={mIdx}
                          className="border-b border-border/40 last:border-b-0 hover:bg-muted/30 transition-colors"
                        >
                          <td className="text-left text-foreground tabular-nums px-4 py-2.5 whitespace-nowrap">
                            {m.name}
                          </td>

                          {/* Previous Value - Gray/Normal */}
                          <td className="text-right text-foreground tabular-nums px-4 py-2.5 whitespace-nowrap">
                            {m.previous?.value ?? "—"}
                          </td>

                          {/* Current Value - Same Color as Previous (NOT BLUE) */}
                          <td className="text-right text-foreground font-medium tabular-nums px-4 py-2.5 whitespace-nowrap">
                            {m.current?.value ?? "—"}
                          </td>

                          {/* Absolute Change */}
                          <td
                            className={`text-right font-medium tabular-nums px-4 py-2.5 whitespace-nowrap ${
                              isUp
                                ? "text-emerald-600 dark:text-emerald-400"
                                : "text-red-600 dark:text-red-400"
                            }`}
                          >
                            {isUp ? "+" : ""}
                            {m.variance?.absolute?.value}
                          </td>

                          {/* Percentage */}
                          <td
                            className={`text-center font-medium tabular-nums px-4 py-2.5 whitespace-nowrap ${
                              isUp
                                ? "text-emerald-600 dark:text-emerald-400"
                                : "text-red-600 dark:text-red-400"
                            }`}
                          >
                            {isUp ? "▲" : "▼"}{" "}
                            {m.variance?.percentage != null &&
                              `${Math.abs(Number(m.variance.percentage)).toFixed(2)}%`}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </div>
          ))}
        </div>

        {/* Notice Message */}
        {d.notice && (
          <div className="mt-3 p-3 bg-blue-500/10 border border-blue-200 dark:border-blue-900 rounded-lg">
            <p className="text-xs text-muted-foreground italic">
              {d.notice}
            </p>
          </div>
        )}
      </div>
    );
  }

  return null;
};

/* ---------------------- TREND HIGHLIGHT (rise / drop) --------------------- */
const TrendMoveCard = ({ h, title, isMax }) => {
  const isUp = h.direction === "up";
  const Icon = isUp ? TrendingUp : TrendingDown;
  const drivers = Array.isArray(h.drivers) ? h.drivers : [];

  return (
    <div
      className={`rounded-xl border px-3 py-2.5 ${
        isUp
          ? "bg-emerald-500/10 border-emerald-600/25"
          : "bg-red-500/10 border-red-500/25"
      }`}
    >
      <p className="text-[11px] text-muted-foreground">{title}</p>

      <p
        className={`flex items-center gap-1.5 font-semibold tabular-nums ${
          isMax ? "text-base" : "text-sm"
        } ${
          isUp
            ? "text-emerald-700 dark:text-emerald-400"
            : "text-red-600 dark:text-red-400"
        }`}
      >
        <Icon size={isMax ? 16 : 14} className="flex-shrink-0" />
        <span>
          {h.label}
          {h.change?.value != null &&
            ` · ${isUp ? "+" : "-"}${h.change.value}`}
          {h.percentage != null &&
            ` (${Math.abs(Number(h.percentage)).toFixed(2)}%)`}
        </span>
      </p>

      {drivers.length > 0 && (
        <div className="mt-2 space-y-1">
          {drivers.map((dr, i) => {
            const drUp = dr.direction === "up";
            return (
              <div
                key={`${dr.name}-${i}`}
                className="flex items-center justify-between gap-3 rounded-lg bg-background/70 px-2.5 py-1.5 text-xs"
              >
                <span className="font-medium text-foreground truncate">
                  {dr.name}
                </span>
                <span
                  className={`inline-flex items-center gap-1 tabular-nums flex-shrink-0 ${
                    drUp
                      ? "text-emerald-600 dark:text-emerald-400"
                      : "text-red-500 dark:text-red-400"
                  }`}
                >
                  {drUp ? <TrendingUp size={12} /> : <TrendingDown size={12} />}
                  {dr.change?.value}
                  {dr.change_pct != null &&
                    ` (${Number(dr.change_pct).toFixed(1)}%)`}
                </span>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

/* --------------------------------- TREND --------------------------------- */
const TrendCard = ({ d, isMax }) => {
  const rows = d.rows || [];
  const highlightMetric = d.highlights?.metric;

  // column headers: prefer d.metrics, fall back to keys of first row
  const columns =
    Array.isArray(d.metrics) && d.metrics.length > 0
      ? d.metrics
      : (rows[0]?.values || []).map((v) => v.key);

  // Variance columns sirf tab dikhao jab kisi bhi row me variance aaya ho
  const hasVariance = rows.some(
    (row) => row.variance && row.variance.absolute?.value != null
  );

  const getValue = (row, col) =>
    row.values?.find((v) => v.key === col)?.value ?? "—";

  const cellPad = "px-3 py-2";

  return (
    <div className={isMax ? "space-y-4" : "space-y-3"}>
      <CardHeader type="TREND" title={d.title} isMax={isMax} />

      <div className="overflow-x-auto rounded-xl border border-border/60 cb-scroll">
        <table
          className={`w-full border-collapse ${isMax ? "text-sm" : "text-xs"}`}
        >
          <thead>
            <tr className="bg-muted/60 border-b border-border/60">
              <th className="text-left font-medium px-3 py-2 whitespace-nowrap text-foreground bg-muted">
                Period
              </th>
              {columns.map((col) => (
                <th
                  key={col}
                  className="text-right font-medium px-3 py-2 whitespace-nowrap text-foreground bg-muted"
                >
                  {col}
                </th>
              ))}

              {/* Variance headers — only when variance exists */}
              {hasVariance && (
                <>
                  <th className="text-right font-medium px-3 py-2 whitespace-nowrap text-foreground bg-muted">
                    Change
                  </th>
                  <th className="text-center font-medium px-3 py-2 whitespace-nowrap text-foreground bg-muted">
                    %
                  </th>
                </>
              )}
            </tr>
          </thead>

          <tbody>
            {rows.map((row, i) => {
              const v = row.variance;
              const isUp = v?.direction === "up";
              const colorClass = isUp
                ? "text-emerald-600 dark:text-emerald-400"
                : "text-red-600 dark:text-red-400";

              return (
                <tr
                  key={i}
                  className="border-b border-border/40 last:border-b-0 bg-background/60"
                >
                  <td
                    className={`${cellPad} text-left tabular-nums whitespace-nowrap text-foreground`}
                  >
                    {row.label}
                  </td>

                  {columns.map((col) => (
                    <td
                      key={col}
                      className={`${cellPad} text-right tabular-nums whitespace-nowrap text-foreground`}
                    >
                      {getValue(row, col)}
                    </td>
                  ))}

                  {/* Variance cells — only when variance exists */}
                  {hasVariance && (
                    <>
                      <td
                        className={`${cellPad} text-right font-medium tabular-nums whitespace-nowrap ${
                          v ? colorClass : "text-muted-foreground"
                        }`}
                      >
                        {v?.absolute?.value != null
                          ? `${isUp ? "+" : "-"}${v.absolute.value}`
                          : "—"}
                      </td>
                      <td
                        className={`${cellPad} text-center font-medium tabular-nums whitespace-nowrap ${
                          v ? colorClass : "text-muted-foreground"
                        }`}
                      >
                        {v?.percentage != null
                          ? `${isUp ? "▲" : "▼"} ${Math.abs(
                              Number(v.percentage)
                            ).toFixed(2)}%`
                          : "—"}
                      </td>
                    </>
                  )}
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      

      {/* Highlights — only when present (biggest_rise / biggest_drop) */}
      {(d.highlights?.biggest_rise || d.highlights?.biggest_drop) && (
        <div className={`grid gap-2 ${isMax ? "grid-cols-2" : "grid-cols-1"}`}>
          {d.highlights.biggest_rise && (
            <TrendMoveCard
              h={d.highlights.biggest_rise}
              title="Biggest rise"
              isMax={isMax}
            />
          )}
          {d.highlights.biggest_drop && (
            <TrendMoveCard
              h={d.highlights.biggest_drop}
              title="Biggest drop"
              isMax={isMax}
            />
          )}
        </div>
      )}

      {/* Legacy highlights (highest / lowest) — only when present */}
      {(d.highlights?.highest || d.highlights?.lowest) && (
        <div className="grid grid-cols-2 gap-2">
          {d.highlights.highest && (
            <div className="rounded-lg bg-emerald-500/10 px-3 py-2">
              <p className="text-[11px] text-muted-foreground">
                Highest{highlightMetric ? ` (${highlightMetric})` : ""}
              </p>
              <p
                className={`font-medium text-emerald-700 dark:text-emerald-400 ${
                  isMax ? "text-sm" : "text-xs"
                }`}
              >
                {d.highlights.highest.label} ({d.highlights.highest.value})
              </p>
            </div>
          )}
          {d.highlights.lowest && (
            <div className="rounded-lg bg-red-500/10 px-3 py-2">
              <p className="text-[11px] text-muted-foreground">
                Lowest{highlightMetric ? ` (${highlightMetric})` : ""}
              </p>
              <p
                className={`font-medium text-red-600 dark:text-red-400 ${
                  isMax ? "text-sm" : "text-xs"
                }`}
              >
                {d.highlights.lowest.label} ({d.highlights.lowest.value})
              </p>
            </div>
          )}
        </div>
      )}

      {/* Net — only when present */}
      {d.net?.absolute?.value != null && (
        <div
          className={`flex items-center justify-between gap-3 rounded-xl border px-3 py-2.5 ${
            d.net.direction === "up"
              ? "bg-emerald-500/10 border-emerald-600/25"
              : "bg-red-500/10 border-red-500/25"
          }`}
        >
          <span
            className={`text-muted-foreground ${isMax ? "text-sm" : "text-xs"}`}
          >
            {d.net.direction === "up" ? "Net increase" : "Net decrease"}
          </span>
          <span
            className={`inline-flex items-center gap-1.5 font-semibold tabular-nums ${
              isMax ? "text-base" : "text-sm"
            } ${
              d.net.direction === "up"
                ? "text-emerald-700 dark:text-emerald-400"
                : "text-red-600 dark:text-red-400"
            }`}
          >
            {d.net.direction === "up" ? (
              <TrendingUp size={isMax ? 16 : 14} />
            ) : (
              <TrendingDown size={isMax ? 16 : 14} />
            )}
            {d.net.direction === "up" ? "+" : "-"}
            {d.net.absolute.value}
            {d.net.percentage != null &&
              ` (${Math.abs(Number(d.net.percentage)).toFixed(2)}%)`}
          </span>
        </div>
      )}

      {/* Notice — only when present */}
      {d.notice && (
        <div className="mt-3 p-3 bg-blue-500/10 border border-blue-200 dark:border-blue-900 rounded-lg">
          <p className="text-xs text-muted-foreground italic">{d.notice}</p>
        </div>
      )}
    </div>
  );
};

/* -------------------------------- DISPATCHER ------------------------------ */
const FormattedDataCard = ({ data, isMaximized = false }) => {
  const type = data?.type;
  const d = data?.data;
  if (!type || !d) return null;

  try {
    switch (type) {
      // case "SIMPLE_INFO":
      //   return <SimpleInfoCard d={d} isMax={isMaximized} />;
      case "DIMENSION_COMPARISON":
        return <DimensionComparisonCard d={d} isMax={isMaximized} />;
      case "RANKING":
        return <RankingCard d={d} isMax={isMaximized} />;
      case "ANALYSIS":
        return <AnalysisCard d={d} isMax={isMaximized} />;
      case "COMPARISON":
        return <ComparisonCard d={d} isMax={isMaximized} />;
      case "TREND":
        return <TrendCard d={d} isMax={isMaximized} />;
      default:
        return null;
    }
  } catch (err) {
    // Any unexpected shape — let caller fall back to summary
    console.error("FormattedDataCard render error:", err);
    return null;
  }
};

/* -------------------------------------------------------------------------- */
/*                              FLOATING CHATBOT                              */
/* -------------------------------------------------------------------------- */

const FloatingChatbot = () => {
  const {
    message,
    setMessage,
    listening,
    setListening,
    messages,
    setMessages,
    isOpen,
    setIsOpen,
    isMaximized,
    setIsMaximized,
    isLeftAligned,
    setIsLeftAligned,
    threadId,
    setThreadId,
    currentCube,
    setCurrentCube,
    currentCubeLabel,
    setCurrentCubeLabel,
    availableCubes,
    setAvailableCubes,
    waitingForCubeSelection,
    setWaitingForCubeSelection,
    loading,
    setLoading,
    copiedIdx,
    setCopiedIdx,
  } = useChatbot();

  const BUBBLE_POS_KEY = "chatbotBubblePositionPct";
  const BUBBLE_SIZE = 56;

  const [bubblePos, setBubblePos] = useState(() => {
    try {
      const saved = localStorage.getItem(BUBBLE_POS_KEY);
      return saved ? JSON.parse(saved) : null;
    } catch {
      return null;
    }
  });
  const [viewportSize, setViewportSize] = useState({
    width: window.innerWidth,
    height: window.innerHeight,
  });
  const [isDragging, setIsDragging] = useState(false);

  const recognitionRef = useRef(null);
  const messagesEndRef = useRef(null);
  const containerRef = useRef(null);
  const dragInfo = useRef({ dragging: false, moved: false });
  const navigate = useNavigate();

  const addBotMessage = (msg) =>
    setMessages((prev) => [
      ...prev,
      { sender: "bot", timestamp: nowTime(), ...msg },
    ]);

  const saveAvailableCubes = (cubes) => {
    if (!cubes || cubes.length === 0) return;
    setAvailableCubes(cubes);
  };

  const syncCubes = (response) => saveAvailableCubes(extractCubes(response));

  const handleResponse = (response, retryText = null) => {
    const status = response?.status;

    if (response?.thread_id) setThreadId(response.thread_id);

    setWaitingForCubeSelection(
      status === "SELECT_CUBE" || status === "AMBIGUOUS_MATCH"
    );

    if (status === "SELECT_CUBE") {
      const cubes = extractCubes(response);
      const options = cubes.length > 0 ? cubes : availableCubes;
      if (options.length === 0) setWaitingForCubeSelection(false);

      addBotMessage({
        text: response.message || "Please select a Cube to continue",
        showCubeSelection: true,
        cubes: options,
      });
    } else if (status === "AMBIGUOUS_MATCH") {
      const ambiguousList = toAmbiguousList(response.ambiguous_filters);
      if (ambiguousList.length === 0) setWaitingForCubeSelection(false);

      addBotMessage({
        text:
          response.message ||
          "Multiple matches found. Please specify which one you mean?",
        showCubeSelection: true,
        isAmbiguous: true,
        cubes: ambiguousList,
      });
    } else if (status === "CUBE_SET") {
      setCurrentCube(response.cube);
      setCurrentCubeLabel(response.cube_label);
      addBotMessage({
        text:
          response.message ||
          `${response.cube_label} dataset selected. You can now ask questions.`,
      });
    } else if (status === "CUBE_SWITCHED") {
      setCurrentCube(response.cube);
      setCurrentCubeLabel(response.cube_label);
      addBotMessage({ text: response.message });
    } else if (status === "SUCCESS") {
      setCurrentCube(response.cube);
      addBotMessage({
        text: response.summary || response.message,
        formattedData: response.formatted_data || null,
        sourceOfTruth: response.source_of_truth || null,
      });
    } else if (status === "NEED_CLARIFICATION") {
      addBotMessage({ text: response.summary || response.message });
    } else if (status === "REJECTED") {
      addBotMessage({ text: response.reason });
    } else {
      addBotMessage({
        text: getErrorText(response),
        isError: true,
        ...(retryText && { retryText }),
      });
    }
  };

  const clampPixelPosition = (
    x,
    y,
    width = window.innerWidth,
    height = window.innerHeight
  ) => {
    const maxX = width - BUBBLE_SIZE;
    const maxY = height - BUBBLE_SIZE;
    return {
      x: Math.min(Math.max(x, 8), Math.max(maxX, 8)),
      y: Math.min(Math.max(y, 8), Math.max(maxY, 8)),
    };
  };

  useEffect(() => {
    const handleResize = () =>
      setViewportSize({ width: window.innerWidth, height: window.innerHeight });
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  const bubblePixelPos = bubblePos
    ? clampPixelPosition(
        bubblePos.xPct * viewportSize.width,
        bubblePos.yPct * viewportSize.height,
        viewportSize.width,
        viewportSize.height
      )
    : null;

  const handleDragMove = (e) => {
    if (!dragInfo.current.dragging) return;
    const point = e.touches ? e.touches[0] : e;
    const dx = point.clientX - dragInfo.current.startClientX;
    const dy = point.clientY - dragInfo.current.startClientY;
    if (!dragInfo.current.moved && Math.hypot(dx, dy) < 4) return;
    e.preventDefault();
    dragInfo.current.moved = true;
    const newX = point.clientX - dragInfo.current.offsetX;
    const newY = point.clientY - dragInfo.current.offsetY;
    const clamped = clampPixelPosition(newX, newY);
    setBubblePos({
      xPct: clamped.x / window.innerWidth,
      yPct: clamped.y / window.innerHeight,
    });
  };

  const handleDragEnd = () => {
    dragInfo.current.dragging = false;
    setIsDragging(false);
    document.removeEventListener("mousemove", handleDragMove);
    document.removeEventListener("mouseup", handleDragEnd);
    document.removeEventListener("touchmove", handleDragMove);
    document.removeEventListener("touchend", handleDragEnd);

    if (dragInfo.current.moved) {
      setBubblePos((current) => {
        if (current)
          localStorage.setItem(BUBBLE_POS_KEY, JSON.stringify(current));
        return current;
      });
    }
  };

  const handleDragStart = (e) => {
    const point = e.touches ? e.touches[0] : e;
    const rect = containerRef.current.getBoundingClientRect();
    setIsDragging(true);
    dragInfo.current = {
      dragging: true,
      moved: false,
      startClientX: point.clientX,
      startClientY: point.clientY,
      offsetX: point.clientX - rect.left,
      offsetY: point.clientY - rect.top,
    };
    document.addEventListener("mousemove", handleDragMove);
    document.addEventListener("mouseup", handleDragEnd);
    document.addEventListener("touchmove", handleDragMove, { passive: false });
    document.addEventListener("touchend", handleDragEnd);
  };

  const handleBubbleClick = () => {
    if (dragInfo.current.moved) {
      dragInfo.current.moved = false;
      return;
    }
    toggleChat();
  };

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages, loading]);

  const startListening = () => {
    if (!("webkitSpeechRecognition" in window)) {
      alert("Speech Recognition not supported in this browser");
      return;
    }

    const recognition = new window.webkitSpeechRecognition();
    recognition.lang = "en-US";
    recognition.continuous = false;
    recognition.interimResults = false;

    recognition.onstart = () => setListening(true);
    recognition.onresult = (event) => {
      const transcript = event.results[0][0].transcript;
      setMessage((prev) => (prev + " " + transcript).trim());
    };
    recognition.onend = () => setListening(false);
    recognition.onerror = () => {
      setListening(false);
      alert("Speech recognition error occurred");
    };

    recognition.start();
    recognitionRef.current = recognition;
  };

  const handleCubeSelection = async (cubeName, isAmbiguous = false) => {
    if (loading) return;

    const payload = isAmbiguous
      ? { thread_id: threadId, prompt: cubeName }
      : { thread_id: threadId, selected_cube: cubeName };

    setLoading(true);
    try {
      const response = await apiCall(
        "post",
        "/processPrompt",
        payload,
        navigate,
        false,
        false
      );
      syncCubes(response);
      handleResponse(response);
    } catch (err) {
      console.error("Cube selection error:", err);
      addBotMessage({
        text: "Sorry, something went wrong. Please try again.",
        isError: true,
      });
    } finally {
      setLoading(false);
    }
  };

  const buildCubeNavState = (sourceOfTruth) => {
    if (!sourceOfTruth) return null;
    return {
      server: sourceOfTruth.server,
      cube: sourceOfTruth.cube,
      dimensions: {
        rows: sourceOfTruth.rows || [],
        columns: sourceOfTruth.columns || [],
        filters: sourceOfTruth.where || [],
      },
    };
  };

  const handleSend = async (retryText = null) => {
    const textToSend = retryText || message.trim();
    if (!textToSend) return;

    if (!retryText) {
      setMessages((prev) => [
        ...prev,
        {
          text: textToSend,
          sender: "user",
          timestamp: nowTime(),
        },
      ]);
      setMessage("");
    }

    setLoading(true);

    try {
      const payload = {
        prompt: textToSend,
        ...(threadId && { thread_id: threadId }),
      };

      const response = await apiCall(
        "post",
        "/processPrompt",
        payload,
        navigate,
        false,
        false
      );
      syncCubes(response);
      handleResponse(response, textToSend);
    } catch (err) {
      console.error("API error:", err);
      addBotMessage({
        text: "Sorry, something went wrong. Please try again.",
        isError: true,
        retryText: textToSend,
      });
    } finally {
      setLoading(false);
    }
  };

  const handleRetry = (retryText) => {
    const filtered = messages.filter(
      (m) => !m.isError || m.retryText !== retryText
    );
    setMessages(filtered);
    handleSend(retryText);
  };

  const handleSwitchCube = async (cubeName) => {
    if (loading) return;

    const payload = {
      thread_id: threadId,
      switch_cube: cubeName,
    };

    setLoading(true);
    try {
      const response = await apiCall(
        "post",
        "/processPrompt",
        payload,
        navigate,
        false,
        false
      );
      syncCubes(response);
      handleResponse(response);
    } catch (err) {
      console.error("Switch cube error:", err);
      addBotMessage({
        text: "Sorry, couldn't switch the dataset. Please try again.",
        isError: true,
      });
    } finally {
      setLoading(false);
    }
  };

/* ---------------------- FORMATTED DATA -> PLAIN TEXT ---------------------- */
const formattedToText = (fd) => {
  const d = fd?.data;
  if (!d) return "";
  const lines = [];

  const pct = (v) =>
    v != null && Number.isFinite(Number(v))
      ? ` (${Number(v).toFixed(2)}%)`
      : "";

  const pushNotice = () => {
    if (d.notice) {
      lines.push("");
      lines.push(`Note: ${d.notice}`);
    }
  };

  const pushItems = (items, indent = "") => {
    (items || []).forEach((item) => {
      const code = item.code ? ` (${item.code})` : "";
      lines.push(
        `${indent}${item.rank ?? "-"}. ${item.label}${code} — ${item.value}`
      );
    });
  };

  if (d.title) lines.push(d.title);

  switch (fd.type) {
    case "RANKING": {
      const groups = (Array.isArray(d.groups) ? d.groups : []).filter(
        (g) =>
          g &&
          ((Array.isArray(g.streets) && g.streets.length > 0) ||
            (Array.isArray(g.items) && g.items.length > 0))
      );

      if (groups.length > 0) {
        groups.forEach((group) => {
          lines.push("");
          lines.push(
            `${group.label}${
              group.subtotal?.value != null ? ` — ${group.subtotal.value}` : ""
            }`
          );

          const streets =
            Array.isArray(group.streets) && group.streets.length > 0
              ? group.streets
              : [{ label: null, items: group.items || [] }];

          streets.forEach((street) => {
            const items = street.items || [];
            if (street.label) {
              const n = items.length;
              lines.push(
                `  ${street.label} — ${n} ${n === 1 ? "device" : "devices"}`
              );
              pushItems(items, "    ");
            } else {
              pushItems(items, "  ");
            }
          });
        });
      } else {
        pushItems(d.items, "");
      }
      break;
    }

    case "ANALYSIS": {
      if (d.variance?.absolute?.value != null) {
        lines.push(
          `Variance: ${d.variance.absolute.value}${pct(d.variance.percentage)}`
        );
      }

      const prev = d.periods?.previous;
      const curr = d.periods?.current;
      if (prev) lines.push(`${prev.label}: ${prev.value}`);
      if (curr) lines.push(`${curr.label}: ${curr.value}`);

      (Array.isArray(d.driver_groups) ? d.driver_groups : [])
        .filter((g) => Array.isArray(g.drivers) && g.drivers.length > 0)
        .forEach((g) => {
          if (g.title) lines.push(g.title);
          g.drivers.forEach((dr) => {
            const dir = dr.direction === "up" ? "Positive" : "Negative";
            lines.push(
              `  ${dr.name}: ${dr.change?.value ?? ""}${pct(
                dr.change_pct
              )} — ${dir}`
            );
          });
        });

      pushNotice();
      break;
    }

    case "DIMENSION_COMPARISON": {
      const rows = d.rows || [];
      rows.forEach((row) => {
        const vals = (row.values || [])
          .map((v) => `${v.key}: ${v.value}`)
          .join(", ");
        lines.push(`${row.label}: ${vals}`);
      });

      if (d.highlights?.highest) {
        lines.push(
          `Highest: ${d.highlights.highest.label} (${d.highlights.highest.value})`
        );
      }
      if (d.highlights?.lowest) {
        lines.push(
          `Lowest: ${d.highlights.lowest.label} (${d.highlights.lowest.value})`
        );
      }

      if (d.truncated?.message) lines.push(d.truncated.message);
      pushNotice();
      break;
    }

    case "TREND": {
      const rows = d.rows || [];
      const columns =
        Array.isArray(d.metrics) && d.metrics.length > 0
          ? d.metrics
          : (rows[0]?.values || []).map((v) => v.key);

      rows.forEach((row) => {
        const vals = columns
          .map(
            (col) =>
              `${col}: ${row.values?.find((v) => v.key === col)?.value ?? "—"}`
          )
          .join(", ");

        const change =
          row.variance?.absolute?.value != null
            ? ` | Change: ${row.variance.direction === "up" ? "+" : "-"}${
                row.variance.absolute.value
              }${pct(row.variance.percentage)}`
            : "";

        lines.push(`${row.label}: ${vals}${change}`);
      });

      const rise = d.highlights?.biggest_rise;
      const drop = d.highlights?.biggest_drop;
      [
        ["Biggest rise", rise],
        ["Biggest drop", drop],
      ].forEach(([title, h]) => {
        if (!h) return;
        lines.push("");
        lines.push(
          `${title}: ${h.label}${
            h.change?.value != null
              ? ` · ${h.direction === "up" ? "+" : "-"}${h.change.value}`
              : ""
          }${pct(h.percentage)}`
        );
        (Array.isArray(h.drivers) ? h.drivers : []).forEach((dr) => {
          lines.push(
            `  ${dr.name}: ${dr.change?.value ?? ""}${pct(dr.change_pct)}`
          );
        });
      });

      if (d.highlights?.highest) {
        lines.push(
          `Highest${d.highlights.metric ? ` (${d.highlights.metric})` : ""}: ${
            d.highlights.highest.label
          } (${d.highlights.highest.value})`
        );
      }
      if (d.highlights?.lowest) {
        lines.push(
          `Lowest${d.highlights.metric ? ` (${d.highlights.metric})` : ""}: ${
            d.highlights.lowest.label
          } (${d.highlights.lowest.value})`
        );
      }

      if (d.net?.absolute?.value != null) {
        lines.push("");
        lines.push(
          `${d.net.direction === "up" ? "Net increase" : "Net decrease"}: ${
            d.net.direction === "up" ? "+" : "-"
          }${d.net.absolute.value}${pct(d.net.percentage)}`
        );
      }

      pushNotice();
      break;
    }

    case "COMPARISON": {
      const pLabel = d.periods?.previous ?? "Previous";
      const cLabel = d.periods?.current ?? "Current";

      const writeMetrics = (metrics) =>
        (metrics || []).forEach((m) => {
          lines.push(
            `  ${m.name}: ${pLabel} ${m.previous?.value ?? "—"} → ${cLabel} ${
              m.current?.value ?? "—"
            } | Change: ${m.variance?.absolute?.value ?? "—"}${pct(
              m.variance?.percentage
            )}`
          );
        });

      if (Array.isArray(d.groups) && d.groups.length > 0) {
        d.groups.forEach((g) => {
          lines.push("");
          lines.push(g.label);
          writeMetrics(g.metrics);
        });
      } else {
        writeMetrics(d.metrics);
      }

      pushNotice();
      break;
    }

    default:
      break;
  }

  return lines.join("\n");
};

const copyToClipboard = (msg, idx) => {
  let textToCopy = msg.text || "";

  if (msg.formattedData && hasRenderableFormat(msg.formattedData)) {
    textToCopy = formattedToText(msg.formattedData);
  }

  if (msg.showCubeSelection && msg.cubes?.length >= 1) {
    const cubeList = msg.cubes
      .map((cubeObj, i) => `${i + 1}. ${cubeObj.label}`)
      .join("\n");
    textToCopy += `\n\n${cubeList}`;
  }

  navigator.clipboard.writeText(textToCopy).then(() => {
    setCopiedIdx(idx);
    setTimeout(() => setCopiedIdx(null), 2000);
  });
};

  const toggleChat = () => {
    setIsOpen((prev) => !prev);

    if (!isOpen && messages.length === 0) {
      setTimeout(() => {
        setMessages([
          {
            text: "Welcome to Codnize AI. This AI is trained on Parking and Enforcement Data, Employee Performance Data, and Off-Street Parking Details. Kindly ensure your questions are based on these data cubes only.",
            sender: "bot",
            timestamp: nowTime(),
          },
        ]);

        if (!threadId && !currentCube) {
          handleSend("HI");
        }
      }, 300);
    }
  };

  const useCustomBubblePos = !isOpen && bubblePos;

  const lastSelectionIdx = messages.reduce(
    (acc, m, i) => (m.showCubeSelection ? i : acc),
    -1
  );

  return (
    <div
      ref={containerRef}
      className={`fixed z-50 ${
        useCustomBubblePos
          ? ""
          : `bottom-4 sm:bottom-6 ${
              isLeftAligned ? "left-4 sm:left-6" : "right-4 sm:right-6"
            }`
      }`}
      style={
        useCustomBubblePos
          ? { left: bubblePixelPos.x, top: bubblePixelPos.y }
          : undefined
      }
    >
      {isOpen && (
        <div
          className={`
            bg-card border border-border rounded-2xl shadow-2xl flex flex-col overflow-hidden
            transition-all duration-300 ease-in-out
            ${
              isMaximized
                ? "fixed inset-0 rounded-none"
                : "w-full max-w-sm md:max-w-md h-[85vh]"
            }
          `}
        >
          <div
            className="
              flex-shrink-0 bg-primary text-primary-foreground
              px-4 py-3 sm:py-4
              flex items-center justify-between
              sticky top-0 z-20 border-b border-primary/20
            "
          >
            <div className="flex items-center gap-3">
              <div className="w-9 h-9 sm:w-10 sm:h-10 rounded-full bg-primary-foreground/20 flex items-center justify-center">
                <img src={Chatbot} alt="logo" className="w-5 h-5 object-contain" />
              </div>
              <div>
                <h3 className="font-semibold text-base sm:text-lg">
                  Codnize AI Assistant
                </h3>
                {currentCubeLabel && (
                  <p className="text-xs opacity-80">{currentCubeLabel}</p>
                )}
              </div>
            </div>

            <div className="flex items-center gap-1 sm:gap-2">
              {!isMaximized && (
                <button
                  onClick={() => setIsLeftAligned(!isLeftAligned)}
                  className="p-2 hover:bg-primary-foreground/10 rounded-lg transition-colors"
                  title="Toggle side"
                >
                  {isLeftAligned ? <ArrowRight size={20} /> : <ArrowLeft size={20} />}
                </button>
              )}

              <button
                onClick={() => setIsMaximized(!isMaximized)}
                className="p-2 hover:bg-primary-foreground/10 rounded-lg transition-colors"
                title={isMaximized ? "Restore" : "Maximize"}
              >
                {isMaximized ? <Minimize2 size={20} /> : <Maximize2 size={20} />}
              </button>

              <button
                onClick={toggleChat}
                className="p-2 hover:bg-primary-foreground/10 rounded-lg transition-colors"
                title="Close"
              >
                <X size={20} />
              </button>
            </div>
          </div>

          <div className="flex-1 overflow-y-auto bg-muted/30 px-3 sm:px-4 py-4 cb-scroll">
            {messages.map((msg, idx) => {
              const showFormatted =
                msg.sender === "bot" && hasRenderableFormat(msg.formattedData);

              return (
                <div
                  key={idx}
                  className={`flex mb-4 ${
                    msg.sender === "user" ? "justify-end" : "justify-start"
                  }`}
                >
                  <div
                    className={`group relative ${
                      showFormatted
                        ? "max-w-[92%] sm:max-w-[88%] w-full"
                        : "max-w-[85%] sm:max-w-[80%]"
                    } ${msg.isError ? "animate-pulse" : ""}`}
                  >
                    <div
                      className={`
                        px-4 py-3 rounded-2xl text-sm leading-relaxed shadow-sm
                        ${
                          msg.sender === "user"
                            ? "bg-primary text-primary-foreground rounded-br-none"
                            : "bg-card border border-border rounded-bl-none"
                        }
                        ${
                          msg.isError
                            ? "border-red-400 bg-red-50/50 dark:bg-red-950/30"
                            : ""
                        }
                      `}
                    >
                      {showFormatted ? (
                        <FormattedDataCard
                          data={msg.formattedData}
                          isMaximized={false}
                        />
                      ) : (
                        <p className="whitespace-pre-wrap">{msg.text}</p>
                      )}

                      {msg.showCubeSelection && msg.cubes && (
                        <div className="mt-3 space-y-2">
                          {msg.cubes.map((cubeObj, i) => (
                            <button
                              key={i}
                              onClick={() =>
                                handleCubeSelection(cubeObj.cube, msg.isAmbiguous)
                              }
                              disabled={
                                idx !== lastSelectionIdx ||
                                !waitingForCubeSelection ||
                                loading
                              }
                              className="w-full text-left px-4 py-2.5 text-sm bg-primary/10 hover:bg-primary/20 rounded-lg transition-colors border border-primary/20 disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:bg-primary/10"
                            >
                              {i + 1}. {cubeObj.label}
                            </button>
                          ))}
                        </div>
                      )}

                      {msg.isError && msg.retryText && (
                        <button
                          onClick={() => handleRetry(msg.retryText)}
                          className="mt-3 flex items-center gap-1.5 text-sm text-red-600 dark:text-red-400 hover:underline"
                        >
                          <RotateCcw size={14} /> Retry
                        </button>
                      )}

                      {msg?.sourceOfTruth &&
                        msg?.sourceOfTruth?.intent !== "WHY" && (
                          <button
                            onClick={() =>
                              navigate("/cube", {
                                state: buildCubeNavState(msg.sourceOfTruth),
                              })
                            }
                            className="mt-3 flex items-center gap-1.5 text-xs text-primary hover:underline font-medium"
                            title="View source data in Cube"
                          >
                            <ExternalLink size={13} />
                            View in Cube
                          </button>
                        )}
                    </div>

                    <button
                      onClick={() => copyToClipboard(msg, idx)}
                      className="absolute -top-1 -right-1 opacity-0 group-hover:opacity-100 p-1.5 bg-background rounded-full shadow border transition-opacity"
                    >
                      <Copy size={14} className="text-muted-foreground" />
                    </button>

                    {copiedIdx === idx && (
                      <span className="absolute -top-1 right-6 text-xs text-green-600 bg-white px-2 py-1 rounded-md shadow">
                        Copied! <Check size={12} className="inline" />
                      </span>
                    )}

                    <div
                      className={`text-xs text-muted-foreground mt-1 px-1 ${
                        msg.sender === "user" ? "text-right" : "text-left"
                      }`}
                    >
                      {msg.timestamp}
                    </div>
                  </div>
                </div>
              );
            })}

            {loading && (
              <div className="flex justify-start">
                <div className="bg-muted px-4 py-3 rounded-2xl">
                  <div className="flex gap-1.5">
                    <div className="w-2 h-2 rounded-full bg-muted-foreground animate-bounce" />
                    <div className="w-2 h-2 rounded-full bg-muted-foreground animate-bounce delay-150" />
                    <div className="w-2 h-2 rounded-full bg-muted-foreground animate-bounce delay-300" />
                  </div>
                </div>
              </div>
            )}

            <div ref={messagesEndRef} />
          </div>

          <div className="flex-shrink-0 border-t border-border bg-card p-3 sm:p-4">
            {currentCube &&
              !waitingForCubeSelection &&
              availableCubes.length > 0 && (
                <div className="mb-3 flex gap-2 overflow-x-auto pb-2 cb-scroll">
                  <span className="text-xs text-muted-foreground whitespace-nowrap self-center">
                    Switch to:
                  </span>
                  {availableCubes
                    .filter((c) => c.cube !== currentCube)
                    .map((cubeObj) => (
                      <button
                        key={cubeObj.cube}
                        onClick={() => handleSwitchCube(cubeObj.cube)}
                        disabled={loading}
                        className="px-3 py-1.5 text-xs bg-secondary hover:bg-secondary/80 rounded-full transition-colors whitespace-nowrap flex-shrink-0 disabled:opacity-50"
                      >
                        {cubeObj.label}
                      </button>
                    ))}
                </div>
              )}

            <div className="flex items-end gap-2">
              <input
                type="text"
                value={message}
                onChange={(e) => setMessage(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === "Enter" && !e.shiftKey) {
                    e.preventDefault();
                    handleSend();
                  }
                }}
                placeholder="Ask anything..."
                disabled={waitingForCubeSelection || loading}
                className="flex-1 bg-background border border-input rounded-xl px-4 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-ring resize-none min-h-[48px]"
              />

              <button
                onClick={() => handleSend()}
                disabled={!message.trim() || waitingForCubeSelection || loading}
                className="p-3 rounded-xl bg-primary text-primary-foreground hover:bg-primary/90 disabled:opacity-50 transition-colors"
              >
                <Send size={20} />
              </button>

              <button
                onClick={startListening}
                disabled={waitingForCubeSelection || loading}
                className={`
                  p-3 rounded-xl border transition-all
                  ${
                    listening
                      ? "bg-destructive text-white border-destructive animate-pulse"
                      : "bg-background border-input hover:bg-accent"
                  }
                  disabled:opacity-50
                `}
              >
                <Mic size={20} />
              </button>
            </div>
          </div>
        </div>
      )}

      {!isOpen && (
        <button
          onClick={handleBubbleClick}
          onMouseDown={handleDragStart}
          onTouchStart={handleDragStart}
          title="Click to chat, drag to move"
          className="w-10 h-10 rounded-full bg-primary text-primary-foreground shadow-2xl hover:scale-110 active:scale-95 transition-all duration-200 flex items-center justify-center group touch-none select-none"
          style={{ cursor: isDragging ? "move" : "default" }}
        >
          <img
            src={Chatbot}
            alt="logo"
            className="w-6 h-6 object-contain group-hover:animate-bounce"
            draggable={false}
          />
        </button>
      )}
    </div>
  );
};

export default FloatingChatbot;